import { useMemo, useState } from "react";
import { Row, Table } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useFetchData } from "../../../../../../lib/hooks/useDefaultFetchData";
import ExtUserPaymentHistoryService from "../../../../../../services/accounts/externalUserPaymentHistory";
import { useFetchDataCount } from "../../../../../../lib/hooks/useDefaultFetchDataCount";
import { useDefaultPaymentHistoryColumns } from "../../../../../../lib/hooks/useDefaultPaymentHistoryColumns";
import { TablePagination } from "../../../../../../components/TablePagination";
import { Header } from "./components/Header";
import { useSorter } from "../../Admin/PaymentHistory/hooks/useSorter";
import { ITaxInfo } from "./types";
import { useGetNestedTableColumns } from "./hooks/useGetNestedTableColumns";

type PaymentHistoryProps = {
  accountNumber: number;
  roleInfo: any;
  taxInfo: ITaxInfo | null;
};

export const PaymentHistory = ({
  accountNumber,
  taxInfo,
  roleInfo,
}: PaymentHistoryProps) => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()]);

  const {
    total,
    isLoading: isCountLoading,
    onPageChange,
    request,
    setRequest,
  } = useFetchDataCount<{
    contractNumber: number;
    yearFrom: number;
    yearTo: number;
    sort?: number;
  }>(
    ExtUserPaymentHistoryService,
    {
      contractNumber: accountNumber,
      yearFrom: range[0].year(),
      yearTo: range[1].year(),
    },
    ["contractNumber", "yearFrom", "yearTo"],
    50,
  );

  const { data, isLoading } = useFetchData<any, any>(
    ExtUserPaymentHistoryService,
    request,
  );

  const tableScroll = useMemo(() => ({ x: "88%" }), []);

  const { columns } = useDefaultPaymentHistoryColumns();

  const { columns: nestedColumns } = useGetNestedTableColumns(roleInfo);

  const expandableRowProps = useMemo(() => {
    return {
      rowExpandable: (record: any) => record.disbursements.length !== 0,
      expandedRowRender: (record: any) => {
        const data: any[] = record.disbursements.map(
          (item: any, i: number) => ({
            ...item,
            i: `${record.id}_${i}`,
          }),
        );

        return (
          <Table
            columns={nestedColumns}
            dataSource={data}
            pagination={false}
            rowKey="i"
            className="payment-history-disb-table mt-3 mb-2 small-table"
            rowClassName={(record) => (record.method === 9 ? "gray-row" : "")}
          />
        );
      },
    };
  }, [nestedColumns]);

  const { onSortChange } = useSorter(request, setRequest);

  return (
    <div className="h-100 payment-history-wrapper px-8">
      <Header
        range={range}
        accountNumber={accountNumber}
        taxInfo={taxInfo}
        setRange={setRange}
      />
      <Row className="d-block history-table__container mt-4 w-100">
        <Table
          dataSource={data}
          columns={columns}
          pagination={false}
          loading={isLoading}
          expandable={expandableRowProps}
          className="overflow-y-auto small-table"
          rowKey={"id"}
          scroll={tableScroll}
          onChange={onSortChange}
        />
        {total > 0 && (
          <TablePagination
            total={total}
            isTotalLoading={isCountLoading}
            onPageChange={onPageChange}
            pageSize={request.pageSize}
            currentPage={request.pageNumber}
          />
        )}
      </Row>
    </div>
  );
};
